import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { StickyTabsProps } from './StickyTabs.d'
import Tab from '@toasttab/shared/src/components/atoms/Tab/Tab'

const StickyTab = ({
  classes,
  isInnovationRelease,
  pagePosition,
  tabLinks,
  testId,
  uniqueId
}: StickyTabsProps) => {
  const ref = useRef(null)
  const [activeTab, setActiveTab] = useState<number>(0)
  const [tabElements, setTabElements] = useState<any>([])
  const [Sections, setSections] = useState<any>([])
  const [windowLocation, setWindowLocation] = useState<string>('')

  const stickyTabClasses = classNames(
    classes,
    'sticky pl-2 overflow-scroll no-scrollbar text-center md:pl-0 z-40',
    {
      'mb-6': !isInnovationRelease,
      '-mb-10': isInnovationRelease,
      // todo - account for AnnouncementBanner if present
      'top-[4rem] md:top-[6rem]': pagePosition === 'Top',
      'top-[90%]': pagePosition === 'Bottom'
    }
  )

  useEffect(() => {
    const tabEls = tabLinks?.map((child: any) => {
      return document.querySelector(`[data-link="${child.ctaLink}"]`)
    })
    const sections = tabLinks?.map((child: any) => {
      return document.querySelector(child.ctaLink)
    })
    setSections(sections)
    setTabElements(tabEls)
    if (typeof window !== 'undefined') {
      setWindowLocation(
        window.location?.origin +
          window.location?.pathname +
          window.location?.search
      )
    }
  }, [])

  useEffect(() => {
    const options = {
      rootMargin: '10px',
      threshold: isInnovationRelease ? 0.25 : 0.1
    }
    if ('IntersectionObserver' in window) {
      initIntersectionObserver(Sections, options)
    }
  }, [Sections, ref])

  const initIntersectionObserver = (observedElements: any, options: any) => {
    const inViewCallback = (entries: any) => {
      entries.forEach((entry: any) => {
        if (entry.isIntersecting) {
          // Scroll horizontally so the active tab is in view
          const tabIndex = tabElements.findIndex(
            (el: any) => el.getAttribute('data-link') === `#${entry.target.id}`
          )
          const tabPosition = tabElements[tabIndex].getBoundingClientRect()
          const scrollOptions = { top: 0, behavior: 'smooth' }
          // Different handlers for different tab positions
          if (tabIndex === 0) {
            // Scroll all the way left if this is the first tab
            ref?.current?.scrollTo({ ...scrollOptions, left: 0 })
          }
          if (tabIndex < tabElements.length - 1) {
            // Center the active tab if we're anywhere in the middle
            ref?.current?.scrollTo({
              ...scrollOptions,
              left: (tabPosition.left + tabPosition.right) / 2
            })
          } else {
            // Scroll to the end of the container if this is the last tab
            ref?.current?.scrollTo({
              ...scrollOptions,
              left: tabPosition.right
            })
          }
          // Set the active tab
          setActiveTab(observedElements.indexOf(entry.target))
        }
      })
    }
    const observer = new IntersectionObserver(inViewCallback, options)
    observedElements?.forEach((element: any) => {
      if (element) {
        observer.observe(element)
      }
    })
  }

  const clickHandler = (index: number) => {
    const newUrl = windowLocation + tabLinks[index].ctaLink
    window.history.replaceState({}, document.title, newUrl)

    let scrollTop
    // Use the original logic for the Innovation Release page, so we don't impact anything currently in use
    if (isInnovationRelease) {
      // Calculate the top position of the section, accounting for padding, so no content is hidden behind the sticky tab
      const sectionTop = Sections[index].getBoundingClientRect().top
      const sectionPadding = Number(
        window
          .getComputedStyle(Sections[index].firstElementChild)
          .paddingTop.replace(/\D/g, '')
      )
      scrollTop = sectionTop + window.scrollY - sectionPadding
      // Need to account for some extra spacing on desktop only.
      // Currently this is tailored to the Innovation Release component, but could be made more generic using a callback function passed as a prop.
      // As these are stacked components with a bottom margin, we'll take that bottom margin to account for spacing between sections.
      // This will display the full heading without the sticky tabs covering any content.
      if (window.innerWidth >= 768) {
        // Use both padding and margin to determine total space between sections
        const previousMargin = Number(
          window
            .getComputedStyle(Sections[index])
            .marginBottom.replace(/\D/g, '')
        )
        const previousPadding = Number(
          window
            .getComputedStyle(Sections[index])
            .paddingBottom.replace(/\D/g, '')
        )
        const previousSpacing = previousMargin + previousPadding
        scrollTop -= previousSpacing
      }
    } else {
      // For new pages, scroll so the middle of the tabs aligns with the bottom of the previous section
      // First find the bottom of the previous section
      const sectionTop = Sections[index]?.getBoundingClientRect().top
      // Then get the height and top position of the tab component
      const tabsHeight = ref.current?.offsetHeight
      const tabsTop = Number(
        window.getComputedStyle(ref.current).top.replace(/\D/g, '')
      )
      // Calculate scroll position based on the top of the section and the current scrollY position
      // Then use the tabs top value and the height to find the midpoint of the tabs component
      scrollTop = sectionTop + window.scrollY - tabsTop - tabsHeight / 2
    }
    // Using scrollTo rather than scrollIntoView due to issues on mobile
    window.scrollTo({ top: scrollTop, behavior: 'smooth' })
    // Timeout here so the active tab doesn't change until the scroll is complete, to prevent conflicts with the intersection observer above
    setTimeout(() => {
      setActiveTab(index)
    }, 500)
  }

  return (
    <section
      className={stickyTabClasses}
      data-testid={testId}
      id={uniqueId}
      ref={ref}
    >
      <div className='mr-2 p-1.5 bg-neutral rounded-xl border border-gray-30 inline-flex md:mr-0 md:p-2.5'>
        {tabLinks?.map((child: any, index: number) => {
          return (
            <Tab
              classes='whitespace-nowrap md:whitespace-normal'
              dataTestId={`Tab-${index + 1}`}
              heading={child.ctaLabel}
              index={index}
              isActive={index === activeTab}
              key={index}
              link={child.ctaLink}
              onClick={(i: number) => clickHandler(i)}
            />
          )
        })}
      </div>
    </section>
  )
}

export default StickyTab
